import { Route, Router, Set } from '@cedarjs/router'

import AIAssistantHistoryPage from 'src/pages/AIAssistantHistoryPage/AIAssistantHistoryPage'
import AIAssistantRoutingPage from 'src/pages/AIAssistantRoutingPage/AIAssistantRoutingPage'
import AIAssistantSettingsPage from 'src/pages/AIAssistantSettingsPage/AIAssistantSettingsPage'
import AIAssistantTestPage from 'src/pages/AIAssistantTestPage/AIAssistantTestPage'
import AIAssistantTrainPage from 'src/pages/AIAssistantTrainPage/AIAssistantTrainPage'
import CommunityAssistantInsightsPage from 'src/pages/CommunityAssistantInsightsPage/CommunityAssistantInsightsPage'
import InspectionInformationPage from 'src/pages/InspectionInformationPage/InspectionInformationPage'
import LetterheadPage from 'src/pages/LetterheadPage/LetterheadPage'
import SettlementPage from 'src/pages/SettlementPage/SettlementPage'
import SettlementsPage from 'src/pages/SettlementsPage/SettlementsPage'

import { useAuth } from './auth'
import AIAdminLayout from './layouts/AIAdminLayout/AIAdminLayout'
import AppLandingLayout from './layouts/AppLandingLayout/AppLandingLayout'
import AppStateContextLayout from './layouts/AppStateContextLayout/AppStateContextLayout'
import AuthGateLayout from './layouts/AuthGateLayout/AuthGateLayout'
import CitizenLayout from './layouts/CitizenLayout/CitizenLayout'
import GovAdminLayout from './layouts/GovAdminLayout/GovAdminLayout'
import GovernmentLayout from './layouts/GovernmentLayout/GovernmentLayout'
import GovNonReviewerLayout from './layouts/GovNonReviewerLayout/GovNonReviewerLayout'
import GovRecordTypeLayout from './layouts/GovRecordTypeLayout/GovRecordTypeLayout'
import GovWellStaffLayout from './layouts/GovWellStaffLayout/GovWellStaffLayout'
import SidebarLayout from './layouts/SidebarLayout/SidebarLayout'

const Routes = () => {
  // FE routes are duplicated in api/src/utils/urls.ts
  return (
    <Router useAuth={useAuth}>
      {/* All routes MUST live within the AppStateContextLayout */}
      <Set wrap={AppStateContextLayout}>
        <Route notfound page={NotFoundPage} />
        <Route path="/404" page={NotFoundPage} name="notFound" />
        <Route path="/signing-up" page={SigningUpPage} name="signingUp" />
        <Route path="/download-app" page={DownloadAppPage} name="downloadApp" />
        <Set wrap={AppLandingLayout}>
          <Route path="/" page={LoginPage} name="home" />
          <Route path="/login" page={LoginPage} name="login" />
          <Route path="/signup" page={SignupPage} name="signUp" />
        </Set>

        <Set wrap={SidebarLayout}>
          {/* All routes that require authentication MUST live within the AuthGateLayout */}
          <Set wrap={AuthGateLayout}>
            {/* GovWellStaff */}
            <Set wrap={GovWellStaffLayout}>
              <Route path="/admin/orgs" page={AdminOrgsPage} name="adminOrgs" />
              <Route path="/admin/orgs/new" page={NewOrgPage} name="newOrg" />
              <Route path="/admin/orgs/{slug:String}" page={AdminOrgPage} name="adminOrg" />
              <Route path="/admin/users" page={AdminUsersPage} name="adminUsers" />
              <Route path="/admin/user-ids-lookup" page={UserIdsLookupPage} name="adminUserIdsLookup" />
            </Set>

            {/* Government-only */}
            <Set wrap={GovernmentLayout}>
              <Set wrap={GovAdminLayout}>
                <Route path="/configure" redirect="modules" />
                <Route path="/document-templates" page={DocumentTemplatesPage} name="documentTemplates" />
                <Route path="/document-templates/{uuid:String}" page={DocumentTemplatePage} name="editDocumentTemplate" />
                <Route path="/email-templates" page={EmailTemplatesPage} name="emailTemplates" />
                <Route path="/email-templates/{id:Int}" page={EmailTemplatePage} name="emailTemplate" />
                <Route path="/settings/fee-schedules" page={FeeSchedulesPage} name="feeSchedules" />
                <Route path="/settings/fee-schedules/{feeScheduleId:Int}" page={FeeScheduleDetailPage} name="feeScheduleDetail" />
                <Route path="/inspection-templates" page={InspectionTemplatesPage} name="inspectionTemplates" />
                <Route path="/inspection-templates/{id:Int}" page={EditInspectionTemplatePage} name="editInspectionTemplate" />
                <Route path="/new-email-template" page={NewEmailTemplatePage} name="newEmailTemplate" />
                <Route path="/organization" page={OrganizationPage} name="organization" />
                <Route path="/record-template/{recordTemplateUuid:String}" page={RecordTemplateConfigurationPage} name="recordTemplateConfiguration" />
                <Route path="/record-type/{recordTypeId:Int}/form" page={EditRecordTypeFormPage} name="editRecordTypeForm" />
                <Route path="/reporting" page={ReportTemplatesPage} name="reporting" />
                <Route path="/task-templates" page={TaskTemplatesPage} name="taskTemplates" />
                <Route path="/task-templates/{taskTemplateId:Int}" page={EditTaskTemplatePage} name="taskTemplate" />
                <Route path="/violation-settings" page={ViolationSettingsPage} name="violationSettings" />
                <Route path="/settings/code-books" page={CodeBookSettingsPage} name="codeBookSettings" />
                <Route path="/settings/code-books/{id:Int}" page={CodeBookEditorPage} name="codeBookEditor" />
                <Route path="/settings/gis" page={GisSettingsPage} name="gis" />
                <Route path="/settings/rule-automations" page={RuleAutomationsPage} name="ruleAutomations" />
                <Route path="/settings/letterheads" page={LetterheadsPage} name="letterheads" />
                <Route path="/settings/letterheads/{uuid:String}" page={LetterheadPage} name="letterhead" />
                <Route path="/settings/meeting-boards" page={MeetingBoardsPage} name="meetingBoards" />
                <Route path="/settings/inspection-calendars" page={InspectionCalendarsPage} name="inspectionCalendars" />
                <Route path="/settings/holidays" page={HolidaysPage} name="holidays" />
                <Route path="/settings/work-orders" page={WorkOrderSettingsPage} name="workOrderSettings" />
              </Set>

              <Set wrap={GovNonReviewerLayout}>
                <Set wrap={GovRecordTypeLayout}>
                  <Route path="/record-type/{recordTypeId:Int}" page={RecordsListPage} name="recordsList" />
                </Set>
                <Route path="/payments" page={PaymentTransfersPage} name="paymentsOld" />
                <Route path="/transactions" page={PaymentTransfersPage} name="paymentTransfers" />
                <Route path="/settlements" page={SettlementsPage} name="settlements" />
                <Route path="/settlements/{settlementUuid:String}" page={SettlementPage} name="settlement" />
                <Route path="/fees" page={RecordTaskFeesPage} name="recordTaskFees" />
                <Route path="/invoices" page={RecordTaskPaymentsPage} name="recordTaskPayments" />
                <Route path="/cases" page={ViolationsPage} name="codeComplaints" />
                <Route path="/cases/{identifier:String}" page={CodeComplaintPage} name="codeComplaint" />
                <Route path="/code-inspections" page={CodeInspectionsPage} name="codeInspections" />
                <Route path="/gis-layers/addresses" page={StaffAddressesPage} name="staffAddresses" />
                <Route path="/gis-layers/parcels" page={StaffParcelsPage} name="staffParcels" />
                <Route path="/gis-layers/other/{entityLayerUuid:String}" page={StaffOtherLayerEntitiesPage} name="staffOtherLayerEntities" />
                <Route path="/addresses" redirect="/gis-layers/addresses" />
                <Route path="/parcels" redirect="/gis-layers/parcels" />
                <Route path="/settings/modules" page={ModulesPage} name="modules" />
                <Route path="/settings/modules/{recordTypeId:Int}" page={ModulePage} name="module" />
                <Route path="/settings/ai/test" page={AIAssistantTestPage} name="aiAssistantTest" />
                <Route path="/settings/ai/history" page={AIAssistantHistoryPage} name="aiAssistantHistory" />
                <Set wrap={AIAdminLayout}>
                  <Route path="/settings/ai/train" page={AIAssistantTrainPage} name="aiAssistantTrain" />
                  <Route path="/settings/ai/routing" page={AIAssistantRoutingPage} name="aiAssistantRouting" />
                  <Route path="/settings/ai" page={AIAssistantSettingsPage} name="aiAssistantSettings" />
                </Set>
                <Route path="/dashboards/{viewDashboardUuid:String}" page={EditViewDashboardPage} name="editViewDashboard" />
                <Route path="/settings/plan-reviews" page={PlanReviewsPage} name="planReviews" />
              </Set>

              <Route path="/calendar" page={CalendarPage} name="calendar" />
              <Route path="/more-use-cases" page={UseCasesPage} name="useCases" />
              <Route path="/company/{companyUuid:String}" page={CompanyPage} name="company" />
              <Route path="/contacts" page={ContactsPage} name="contacts" />
              <Route path="/auto-check/monitor" page={AutoCheckMonitorPage} name="autoCheckMonitor" />
              <Route path="/insights" page={InsightsOverviewPage} name="insights" />
              <Route path="/insights/auto-check" page={InsightsAutoCheckPage} name="insightsAutoCheck" />
              <Route path="/insights/community-assistant" page={CommunityAssistantInsightsPage} name="communityAssistantInsights" />
              <Route path="/insights/inspections" page={InsightsInspectionsPage} name="insightsInspections" />
              <Route path="/insights/community" page={InsightsCommunityPage} name="insightsCommunity" />
              <Route path="/record-collection/{recordCollectionUuid:String}" page={RecordCollectionPage} name="recordCollection" />
              <Route path="/meetings" page={OrganizationMeetingsPage} name="organizationMeetings" />
              <Route path="/meeting/{meetingUuid:String}" page={MeetingPage} name="meeting" />
              <Route path="/locations/address/{addressDisplayName:String}" page={StaffAddressPage} name="staffAddressOverview" />
              <Route path="/locations/parcel/{parcelDisplayName:String}" page={StaffParcelPage} name="staffParcelOverview" />
              <Set wrap={GovRecordTypeLayout}>
                <Route path="/record-type/{recordTypeId:Int}/inspections" page={RecordsInspectionsPage} name="recordsInspections" />
                <Route path="/record-type/{recordTypeId:Int}/plan-reviews" page={RecordsPlanReviewsPage} name="recordsPlanReviews" />
                <Route path="/record-type/{recordTypeId:Int}/documents" page={ModuleDocumentsPage} name="moduleDocuments" />
              </Set>
              <Route path="/stamp-templates" page={StampTemplatesPage} name="stampTemplates" />
              <Route path="/stamp-templates/{uuid:String}" page={StampTemplatePage} name="editStampTemplate" />
              <Route path="/user/{userUuid:String}" page={UserPage} name="user" />
              <Route path="/settings" page={SettingsPage} name="settings" />
            </Set>

            <Set wrap={CitizenLayout}>
              <Route path="/projects" redirect="citizenRecords" />
              <Route path="/records" page={CitizenRecordsPage} name="citizenRecords" />
              <Route path="/inspections" page={CitizenInspectionsPage} name="citizenInspections" />
              <Route path="/payments" page={CitizenPaymentsPage} name="citizenPayments" />
              <Route path="/my-companies" page={MyCompaniesPage} name="myCompanies" />
              <Route path="/company-profile/{uuid:String}" page={CompanyProfilePage} name="viewCompanyProfile" />
            </Set>

            <Route path="/{slug:String}/{recordUuid:String}/edit" page={EditRecordDraftPage} name="editRecordDraft" />
            <Route path="/record/{recordUuid:String}" page={RecordPage} name="record" />

            {/* Document generation routes */}
            <Route path="/record/{recordUuid:String}/generate-documents" page={RecordGenerateDocumentsPage} name="recordGenerateDocuments" />
            <Route path="/record/{recordUuid:String}/generate-task-documents/{taskUuid:String}" page={RecordTaskGenerateDocumentsPage} name="recordTaskGenerateDocuments" />
            <Route path="/record/{recordUuid:String}/regenerate-document/{documentUuid:String}" page={RegenerateDocumentPage} name="regenerateDocument" />

            <Route path="/select-jurisdiction" page={ApplicantCityPickPage} name="applicantCityPick" />
            <Route path="/tasks" page={TasksPage} name="tasks" />
            <Route path="/user-profile" page={UserProfilePage} name="userProfile" />
          </Set>

          {/* TODO ! ! ! organization slugs may conflict with the names of our actual routes ! ! ! need to prefix all external or all internal routes (https://www.notion.so/govwell/Prefix-routes-to-prevent-conflicts-between-internal-and-external-organization-slug-routes-948168cc3d7f41e78f7e3a060e2c2a0b?pvs=4) */}
          <Route path="/{slug:String}/{recordTypeId:Int}/apply" page={RecordTypeApplyPage} name="recordTypePickOld" />
          <Route path="/{slug:String}/workflow/{recordTemplateUuid:String}/apply" page={RecordTemplateApplyPage} name="recordTemplateApply" />
          <Route path="/{slug:String}/{recordTypeId:Int}/pick" page={RecordTemplatePickPage} name="recordTemplatePick" />
          <Route path="/{slug:String}/submit-complaint" page={SubmitComplaintPage} name="submitComplaint" />
          <Route path="/{slug:String}/complaints/submit" page={SubmitComplaintV2Page} name="submitComplaintRecordType" />
          <Route path="/{slug:String}/pay" page={CityPayPage} name="cityPay" />
          <Route path="/{slug:String}/pay/{recordIdentifierOrRecordIssuedIdentifier:String}" page={CityPayPage} name="cityPaySpecific" />
          <Route path="/{slug:String}/search-addresses-and-parcels" page={CitizenAddressesAndParcelsPage} name="citizenSearchAddressesAndParcels" />
        </Set>

        {/* The organization home page is the one exception that should always be a topbar layout, do not move it into the SidebarLayout */}

        {/* TODO ! ! ! organization slugs may conflict with the names of our actual routes ! ! ! need to prefix all external or all internal routes (https://www.notion.so/govwell/Prefix-routes-to-prevent-conflicts-between-internal-and-external-organization-slug-routes-948168cc3d7f41e78f7e3a060e2c2a0b?pvs=4) */}

        <Route path="/{slug:String}" page={PublicPortalPage} name="cityHome" />

        {/** TODO: Wrap all of these unauthenticated routes in SidebarLayout, remove TopbarLayout imports from the pages, allow SidebarLayout to determine whether to fall back to topbar */}
        <Route path="/{slug:String}/chat" page={AIChatPage} name="chat" />
        <Route path="/{slug:String}/cases/track" page={CitizenTrackViolationsPage} name="citizenTrackViolations" />
        <Route path="/{slug:String}/cases/{violationUuid:String}" page={ViolationCitizenPage} name="violationCitizen" />
        <Route path="/{slug:String}/public-records" page={PublicDashboardsPage} name="publicRecords" />
        <Route path="/{slug:String}/public-records/{dashboardUuid:String}" page={PublicDashboardPage} name="publicDashboard" />
        <Route path="/{slug:String}/public-record/{recordUuid:String}" page={PublicRecordPage} name="publicRecord" />
        <Route path="/{slug:String}/inspection-information" page={InspectionInformationPage} name="inspectionInformation" />
        <Route path="/{slug:String}/claim" page={LicenseClaimPage} name="licenseClaim" />

        <Set wrap={SidebarLayout}>
          <Route path="/gis-layers/addresses/{addressUuid:String}" page={EntityAddressPage} name="entityAddress" />
          <Route path="/gis-layers/parcels/{parcelUuid:String}" page={EntityParcelPage} name="entityParcel" />
          <Route path="/gis-layers/other/{entityLayerUuid:String}/{entityUuid:String}" page={OtherLayerEntityDetailPage} name="otherLayerEntityDetail" />
          <Route path="/addresses/{addressUuid:String}" redirect="/gis-layers/addresses/{addressUuid}" />
          <Route path="/parcels/{parcelUuid:String}" redirect="/gis-layers/parcels/{parcelUuid}" />
        </Set>
      </Set>
    </Router>
  )
}

export default Routes
